/**
 * Trie (Prefix Tree) data structure for efficient prefix-based word search
 * Time Complexity: O(m) for search/insert where m is word length
 */

import { WordIndex } from '../../types/dictionary';
import { TrieNode } from '../../types/search';

export class Trie {
  private root: TrieNode;

  constructor() {
    this.root = this.createNode(null);
  }

  /**
   * Creates a new TrieNode
   */
  private createNode(char: string | null): TrieNode {
    return {
      char,
      children: new Map(),
      words: [],
      isEndOfWord: false,
    };
  }

  /**
   * Insert a word into the Trie
   * @param word - WordIndex object to insert
   */
  insert(word: WordIndex): void {
    // Validate input
    if (!word || !word.word || typeof word.word !== 'string' || word.word.length === 0) {
      console.warn('Invalid word object provided to Trie.insert:', word);
      return;
    }

    let current = this.root;
    const wordString = word.word.toLowerCase();

    for (let i = 0; i < wordString.length; i++) {
      const char = wordString[i]!;

      if (!current.children.has(char)) {
        current.children.set(char, this.createNode(char));
      }

      current = current.children.get(char)!;
    }

    current.isEndOfWord = true;
    if (!current.words) {
      current.words = [];
    }
    current.words.push(word);
  }

  /**
   * Search for words with a given prefix
   * @param prefix - Search prefix
   * @param maxResults - Maximum number of results to return
   * @returns Array of matching WordIndex objects
   */
  search(prefix: string, maxResults: number = 20): WordIndex[] {
    const results: WordIndex[] = [];
    const lowerPrefix = prefix.toLowerCase();

    // Navigate to the prefix node
    let current = this.root;
    for (let i = 0; i < lowerPrefix.length; i++) {
      const char = lowerPrefix[i]!;
      if (!current.children.has(char)) {
        return []; // Prefix not found
      }
      current = current.children.get(char)!;
    }

    // Collect all words from this node onwards
    this.collectWords(current, results, maxResults);
    return results;
  }

  /**
   * Recursively collect words from a node
   * @param node - Starting TrieNode
   * @param results - Array to accumulate results
   * @param maxResults - Maximum number of results
   */
  private collectWords(
    node: TrieNode,
    results: WordIndex[],
    maxResults: number
  ): void {
    if (results.length >= maxResults) {
      return;
    }

    // Add words at this node with validation
    if (node.isEndOfWord && node.words && Array.isArray(node.words)) {
      // Filter out any invalid word entries
      const validWords = node.words.filter(word =>
        word && typeof word.word === 'string' && word.word.length > 0
      );
      if (validWords.length > 0) {
        results.push(...validWords);
      }
    }

    if (results.length >= maxResults) {
      return;
    }

    // Recursively collect from children
    if (node.children && node.children.size > 0) {
      for (const [, childNode] of node.children) {
        if (childNode) {
          this.collectWords(childNode, results, maxResults);
          if (results.length >= maxResults) {
            break;
          }
        }
      }
    }
  }

  /**
   * Check if a word exists in the Trie
   * @param word - Word to check
   * @returns true if word exists
   */
  has(word: string): boolean {
    let current = this.root;
    const lowerWord = word.toLowerCase();

    for (let i = 0; i < lowerWord.length; i++) {
      const char = lowerWord[i]!;
      if (!current.children.has(char)) {
        return false;
      }
      current = current.children.get(char)!;
    }

    return current.isEndOfWord;
  }

  /**
   * Get all words in the Trie
   * @returns Array of all WordIndex objects
   */
  getAllWords(): WordIndex[] {
    const results: WordIndex[] = [];
    this.collectWords(this.root, results, Infinity);
    return results;
  }

  /**
   * Get the root node (useful for advanced operations)
   */
  getRoot(): TrieNode {
    return this.root;
  }

  /**
   * Get statistics about the Trie
   */
  getStats(): {
    totalNodes: number;
    totalWords: number;
    maxDepth: number;
  } {
    let totalNodes = 0;
    let totalWords = 0;
    let maxDepth = 0;

    const traverse = (node: TrieNode, depth: number): void => {
      totalNodes++;
      if (node.isEndOfWord) {
        totalWords += node.words.length;
      }
      maxDepth = Math.max(maxDepth, depth);

      for (const [, child] of node.children) {
        traverse(child, depth + 1);
      }
    };

    traverse(this.root, 0);

    return { totalNodes, totalWords, maxDepth };
  }
}
